import type { PlaybackRegion } from '../PlaybackRegions';
import type { TextAlignment } from '../../enums';
import { PlaybackRegions } from '../PlaybackRegions';
import type { Transcript } from '../Transcript';
import { TextStyleRenderer } from './TextAsset/renderer/TextStyleRenderer';
import { TextMetrics } from './TextAsset/metrics/TextMetrics';
import type { CaptionAsset } from './CaptionAsset/asset/CaptionAsset';
import type { FancyCaptionAsset } from './FancyCaptionAsset/asset/FancyCaptionAsset';

/**
 * Converts milliseconds into a timestamp string for SRT or WebVTT cues.
 * SRT uses ',' as the millisecond separator; WebVTT uses '.'.
 * Spec: https://www.w3.org/TR/webvtt1/#webvtt-timestamp
 */
export function convertToTimestamp(milliseconds: number, msSeparator: ',' | '.' = ','): string {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    milliseconds = Math.floor(milliseconds % 1000);
    seconds = seconds % 60;
    minutes = minutes % 60;
    return (
        hours.toString().padStart(2, '0') +
        ':' +
        minutes.toString().padStart(2, '0') +
        ':' +
        seconds.toString().padStart(2, '0') +
        msSeparator +
        milliseconds.toString().padStart(3, '0')
    );
}

/**
 * Backwards-compatible wrapper around {@link convertToTimestamp} that always
 * uses the SRT-style ',' millisecond separator.
 */
export function convertToSRTTimestamp(milliseconds: number): string {
    return convertToTimestamp(milliseconds, ',');
}

function formatChunksAsCues(
    timeOffset: number,
    playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
    captionAssetDuration: number,
    chunks: string[],
    chunkTimeRanges: number[][],
    msSeparator: ',' | '.',
): string[] {
    return chunkTimeRanges.map((timeRange, index) => {
        const [startTime, endTime] = timeRange;
        const playbackStartTime = PlaybackRegions.assetTimeToPlaybackTime(
            playbackRegions,
            captionAssetDuration,
            startTime,
        );
        const playbackEndTime = PlaybackRegions.assetTimeToPlaybackTime(
            playbackRegions,
            captionAssetDuration,
            endTime,
        );

        const startString = convertToTimestamp(playbackStartTime + timeOffset, msSeparator);
        const endString = convertToTimestamp(playbackEndTime + timeOffset, msSeparator);
        return `${startString} --> ${endString}\n${chunks[index]}`;
    });
}

type MetricsBuilder = (settings: {
    transcript: Transcript;
    alignment: TextAlignment;
    style: TextStyleRenderer;
    maxWidth: number;
    maxHeight: number;
}) => TextMetrics[];

export type CaptionFormat = 'srt' | 'vtt';

/**
 * Returns each caption chunk as a formatted cue string (SRT or WebVTT).
 */
export async function buildCuesFromCaptions(
    asset: CaptionAsset | FancyCaptionAsset,
    metricsBuilder: MetricsBuilder,
    isRendererReady: boolean,
    timeOffset: number,
    playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
    captionAssetDuration: number,
    format: CaptionFormat = 'srt',
): Promise<string[]> {
    if (!asset.hasText) {
        return [];
    }

    let chunks: string[];
    let chunkTimeRanges: number[][];

    if (isRendererReady) {
        chunks = asset.renderer.getChunkTexts();
        chunkTimeRanges = asset.renderer.getChunkTimeRanges();
    } else {
        // The renderer metrics may not be built if it's not visible.
        await asset.onUsedFontsLoaded();
        const settings = asset.getRendererSettings();
        const style = new TextStyleRenderer();
        style.apply(settings.normalStyle);

        const metrics = metricsBuilder({
            transcript: asset.transcript,
            alignment: settings.alignment,
            style,
            maxWidth: settings.maxTextWidth,
            maxHeight: settings.maxTextHeight,
        });

        chunks = TextMetrics.getChunkTexts(metrics);
        chunkTimeRanges = TextMetrics.getChunkTimeRanges(metrics, asset.transcript);
    }

    const msSeparator = format === 'vtt' ? '.' : ',';
    return formatChunksAsCues(
        timeOffset,
        playbackRegions,
        captionAssetDuration,
        chunks,
        chunkTimeRanges,
        msSeparator,
    );
}

/**
 * Backwards-compatible wrapper around {@link buildCuesFromCaptions} that
 * produces SRT-formatted cues.
 */
export async function buildSRTFromCaptions(
    asset: CaptionAsset | FancyCaptionAsset,
    metricsBuilder: MetricsBuilder,
    isRendererReady: boolean,
    timeOffset: number,
    playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
    captionAssetDuration: number,
): Promise<string[]> {
    return buildCuesFromCaptions(
        asset,
        metricsBuilder,
        isRendererReady,
        timeOffset,
        playbackRegions,
        captionAssetDuration,
        'srt',
    );
}
